/**
 * One shared Firestore listener per query, outliving the components that use
 * it (D19).
 *
 * Without this, moving between the home and a list screen means unmounting one
 * component and mounting another, each starting its own listener from nothing
 * — so the reader watches a spinner for data the app held a moment ago, and
 * the same query is billed twice while both are mounted.
 *
 * The cache is keyed by query, not by component: whoever mounts first opens
 * the listener, everyone else reads the snapshot already in hand, and the
 * listener stays open for a grace period after the last reader leaves so a
 * navigation does not close and reopen it. Because it is a live listener the
 * cached value is not stale — an owner's change still arrives without a
 * refresh.
 *
 * Deliberately not a general-purpose store: it holds only what the home reads,
 * and every entry is per-uid so one user's snapshot can never be served to
 * another.
 */
import { useCallback, useRef, useSyncExternalStore } from 'react';

export interface LiveSnapshot<T> {
  value: T | null;
  error: Error | null;
}

type Unsubscribe = () => void;

export type LiveSubscribe<T> = (
  onValue: (value: T) => void,
  onError: (error: Error) => void,
) => Unsubscribe;

interface Entry<T> {
  snapshot: LiveSnapshot<T>;
  listeners: Set<() => void>;
  unsubscribe: Unsubscribe | null;
  idleTimer: ReturnType<typeof setTimeout> | null;
}

/**
 * How long the listener survives with no readers. Long enough to cover a
 * navigation — including opening a task and coming back — without holding a
 * connection open for a reader who has left the app.
 */
export const IDLE_GRACE_MS = 60_000;

const cache = new Map<string, Entry<unknown>>();

const EMPTY: LiveSnapshot<never> = { value: null, error: null };

function emit<T>(entry: Entry<T>): void {
  entry.listeners.forEach((listener) => listener());
}

function closeEntry(key: string): void {
  const entry = cache.get(key);
  if (!entry || entry.listeners.size > 0) return;
  entry.unsubscribe?.();
  cache.delete(key);
}

function acquire<T>(key: string, subscribe: LiveSubscribe<T>): Entry<T> {
  let entry = cache.get(key) as Entry<T> | undefined;

  if (!entry) {
    entry = {
      snapshot: EMPTY as LiveSnapshot<T>,
      listeners: new Set(),
      unsubscribe: null,
      idleTimer: null,
    };
    cache.set(key, entry as Entry<unknown>);
    // Replacing the snapshot object rather than mutating it is what lets
    // `useSyncExternalStore` see a change; it compares by identity.
    entry.unsubscribe = subscribe(
      (value) => {
        entry!.snapshot = { value, error: null };
        emit(entry!);
      },
      (error) => {
        entry!.snapshot = { value: entry!.snapshot.value, error };
        emit(entry!);
      },
    );
  }

  if (entry.idleTimer) {
    clearTimeout(entry.idleTimer);
    entry.idleTimer = null;
  }

  return entry;
}

/**
 * Subscribe to a shared live query. `key` identifies the query — pass null
 * when there is nothing to watch (no signed-in user), which reports an empty
 * snapshot without opening a listener.
 *
 * `subscribe` is read only when the listener is actually opened, so it does
 * not need to be memoised.
 */
export function useLiveQuery<T>(key: string | null, subscribe: LiveSubscribe<T>): LiveSnapshot<T> {
  const subscribeRef = useRef(subscribe);
  subscribeRef.current = subscribe;

  const store = useCallback(
    (onStoreChange: () => void) => {
      if (!key) return () => {};

      const entry = acquire<T>(key, (onValue, onError) =>
        subscribeRef.current(onValue, onError),
      );
      entry.listeners.add(onStoreChange);

      return () => {
        entry.listeners.delete(onStoreChange);
        if (entry.listeners.size === 0) {
          entry.idleTimer = setTimeout(() => closeEntry(key), IDLE_GRACE_MS);
        }
      };
    },
    [key],
  );

  const getSnapshot = useCallback((): LiveSnapshot<T> => {
    if (!key) return EMPTY as LiveSnapshot<T>;
    const entry = cache.get(key) as Entry<T> | undefined;
    return entry ? entry.snapshot : (EMPTY as LiveSnapshot<T>);
  }, [key]);

  return useSyncExternalStore(store, getSnapshot, getSnapshot);
}

/** Test seam: drop every listener and cached snapshot. */
export function resetLiveQueryCache(): void {
  cache.forEach((entry, key) => {
    if (entry.idleTimer) clearTimeout(entry.idleTimer);
    entry.unsubscribe?.();
    cache.delete(key);
  });
}
